// AID 分配 REST 客戶端(S31,IA specs/01 §2.2;common/12 v1.1 §4.2.1)。
// 後端契約 = restful /v3/admin/aid-allocations*(restful#74,已在 restful main):
//   GET    /v3/admin/aid-allocations?batch_ref=&owner_scope=&status=&limit=  清單
//   POST   /v3/admin/aid-allocations                                          建立
//   GET    /v3/admin/aid-allocations/{allocation_id}                          單筆
//   PATCH  /v3/admin/aid-allocations/{allocation_id}                          調整計數
//   POST   /v3/admin/aid-allocations/{allocation_id}/revoke                   撤銷
// 時間戳一律微秒(µs since epoch)。reason + case_id 為必填(後端 ErrAdminReasonRequired)。
//
// 超額 / scope / batch 未 release 等 fail-closed 判定在後端:422 AID_ALLOCATION_OVER_PO /
// AID_ALLOCATION_SCOPE_EXCEEDED / AID_BATCH_NOT_RELEASED / AID_BATCH_NOT_FOUND、
// 503 device-ca unavailable。前端不預先放行,直接把後端錯誤誠實呈現(ApiError.message)。

import { apiFetch } from '../../lib/api';

export type AidAllocationStatus = 'active' | 'exhausted' | 'revoked';

/** 對應後端 domain.AidAllocation(control-plane 記帳側;私鑰永不經此)。 */
export interface AidAllocation {
  allocation_id: string;
  batch_ref: string;
  po_ref: string;
  org_id?: string;
  owner_scope: string; // user#{id} | org#{id}
  level_scope: string[];
  model_scope: string[];
  quantity: number;
  allocated: number; // earmark 給 workspace 的 AID 數
  consumed: number; // 已 Activate(device-ca §1.4)—— 永不回收
  status: AidAllocationStatus | string;
  reason?: string;
  created_by?: string;
  created_at: number;
  updated_at: number;
  revoked_by?: string;
  revoked_at?: number;
  revoke_reason?: string;
}

export interface AdminReasonCase {
  reason: string;
  case_id: string;
}

export interface ListAllocationsParams {
  batch_ref?: string;
  owner_scope?: string;
  status?: string;
  limit?: number;
}

/** 建立請求 —— 後端 handler 把 AidAllocationCreate 欄位與 reason/case_id 平攤在頂層。 */
export interface CreateAllocationBody {
  batch_ref: string;
  po_ref: string;
  org_id?: string;
  owner_scope: string;
  level_scope: string[];
  model_scope: string[];
  quantity: number;
}

/** 調整請求 —— 三個計數皆選填(省略=保留);後端以指標欄判斷。 */
export interface AdjustAllocationBody {
  quantity?: number;
  allocated?: number;
  consumed?: number;
}

/** GET /v3/admin/aid-allocations — 清單(空值篩選被 drop)。回傳 {items}。 */
export async function listAllocations(params: ListAllocationsParams = {}): Promise<AidAllocation[]> {
  const res = await apiFetch<{ items?: AidAllocation[] }>('/v3/admin/aid-allocations', {
    query: {
      batch_ref: params.batch_ref,
      owner_scope: params.owner_scope,
      status: params.status,
      limit: params.limit,
    },
  });
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

/** GET /v3/admin/aid-allocations/{id} — 單筆(對帳計數為 device-ca live 值)。 */
export async function getAllocation(allocationId: string): Promise<AidAllocation> {
  const res = await apiFetch<AidAllocation>(`/v3/admin/aid-allocations/${encodeURIComponent(allocationId)}`);
  return res.data;
}

/** POST /v3/admin/aid-allocations — 建立;reason+case_id 必填、audit。 */
export async function createAllocation(body: CreateAllocationBody, audit: AdminReasonCase): Promise<AidAllocation> {
  const res = await apiFetch<AidAllocation>('/v3/admin/aid-allocations', {
    method: 'POST',
    body: { ...body, reason: audit.reason, case_id: audit.case_id },
  });
  return res.data;
}

/** PATCH /v3/admin/aid-allocations/{id} — 調整計數;只送有變動的欄位。 */
export async function adjustAllocation(
  allocationId: string,
  patch: AdjustAllocationBody,
  audit: AdminReasonCase
): Promise<AidAllocation> {
  const body: Record<string, unknown> = { reason: audit.reason, case_id: audit.case_id };
  if (patch.quantity !== undefined) body.quantity = patch.quantity;
  if (patch.allocated !== undefined) body.allocated = patch.allocated;
  if (patch.consumed !== undefined) body.consumed = patch.consumed;
  const res = await apiFetch<AidAllocation>(`/v3/admin/aid-allocations/${encodeURIComponent(allocationId)}`, {
    method: 'PATCH',
    body,
  });
  return res.data;
}

/** POST /v3/admin/aid-allocations/{id}/revoke — 撤銷剩餘 headroom;已 Activate 不受影響。 */
export async function revokeAllocation(allocationId: string, audit: AdminReasonCase): Promise<AidAllocation> {
  const res = await apiFetch<AidAllocation>(`/v3/admin/aid-allocations/${encodeURIComponent(allocationId)}/revoke`, {
    method: 'POST',
    body: audit,
  });
  return res.data;
}
